import { Award, Stethoscope, MapPin } from "lucide-react";

const HIGHLIGHTS = [
  {
    icon: Award,
    title: "Qualified dentists",
    desc: "Every dentist on our panel holds a valid dentist's degree and is trained in safe home-visit protocols.",
  },
  {
    icon: Stethoscope,
    title: "Practical & useful portable equipment",
    desc: "Custom-built mobile dental units, digital X-ray and ultrasonic scalers — designed in-house to deliver real, modern dentistry at your bedside.",
  },
  {
    icon: MapPin,
    title: "Registered dentist",
    desc: "Officially registered with the State Dental Council and committed to high standards of ethics, hygiene and clinical excellence.",
  },
];

export default function About() {
  return (
    <section id="about" className="py-20 md:py-28 bg-brand-bg" data-testid="about-section">
      <div className="container-site">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="overline mb-3">Our story</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-pine leading-tight">
              Why we built a clinic <span className="italic text-brand-clay">that travels.</span>
            </h2>

            <div className="mt-6 space-y-5 text-brand-textMuted leading-relaxed text-[17px] max-w-2xl">
              <p>
                A few years ago, a friend asked me — being a doctor — to come
                home and examine her father, who had suffered a paralytic
                stroke. At her home, her elder brother was the one caring for
                their father, and I was led into the room where he was resting.
              </p>
              <p>
                It was a small room with just a bed, and a wall directly
                opposite it. Upon entering, I noticed several circular stains
                on the wall. When I asked her brother about them, I learned
                something I will never forget: their father experienced such
                excruciating dental pain while eating — pain he simply could
                not bear — that he would spit the morsel of food out of his
                mouth and fling it against the wall. Those stains were the
                result.
              </p>
              <p>
                After 15 years of practising dentistry, that single moment made
                me realise there was still something significant left for me
                to accomplish. Determined to find a solution, I researched the
                market for portable dental setups — but the existing options
                were neither practical nor convenient.
              </p>
              <p>
                So, after a year of dedicated research and development, I
                designed and built my own portable dental setup — one that
                lets me visit a patient's home and provide comprehensive
                dental treatment right at their bedside.
              </p>
              <p className="text-brand-pine font-medium">
                The time has come to reach the patients who truly need this
                service the most. Their suffering cannot be eased by medication
                alone — timely dental treatment is essential. I hope you will
                join hands with me on this mission.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 gap-4">
            {HIGHLIGHTS.map((h, i) => (
              <div
                key={h.title}
                data-testid={`about-highlight-${i}`}
                className="flex gap-4 p-5 rounded-2xl bg-white border border-brand-line/60 hover:shadow-md transition-shadow"
              >
                <div className="w-11 h-11 shrink-0 rounded-full bg-brand-clay/10 text-brand-clay flex items-center justify-center">
                  <h.icon size={20} strokeWidth={1.6} />
                </div>
                <div>
                  <h4 className="font-serif text-xl text-brand-pine">{h.title}</h4>
                  <p className="text-sm text-brand-textMuted mt-1 leading-relaxed">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
